import type { LanguageRegistry } from "../registry";
import type { Detection } from "./types";

/** One model guess: an extension-like label ("py", "rb") with a probability. */
export interface ModelResult {
  languageId: string;
  confidence: number;
}

export interface ContentClassifier {
  classify(content: string): Promise<ModelResult[]>;
  dispose(): void;
}

export interface ContentDetectionOptions {
  /** Below this many non-whitespace characters, don't guess. */
  minLength?: number;
  /** Only the start of large files is classified. */
  maxLength?: number;
  /** Minimum probability of the top guess. */
  threshold?: number;
  /** Top guess must be at least this many times as likely as the runner-up. */
  margin?: number;
}

const DEFAULTS: Required<ContentDetectionOptions> = {
  minLength: 20,
  maxLength: 100_000,
  threshold: 0.3,
  margin: 2,
};

/** Model label → registry id. Labels are mostly file extensions. */
export function resolveModelLabel(label: string, registry: LanguageRegistry): string | undefined {
  return (registry.byExtension(`file.${label}`) ?? registry.get(label))?.id;
}

/** Picks a confident, unambiguous guess from model results, or nothing. */
export function pickLanguage(
  results: readonly ModelResult[],
  registry: LanguageRegistry,
  options: ContentDetectionOptions = {},
): Detection | undefined {
  const { threshold, margin } = { ...DEFAULTS, ...options };
  const [top, second] = [...results].sort((a, b) => b.confidence - a.confidence);
  if (!top || top.confidence < threshold) return undefined;
  if (second && top.confidence < second.confidence * margin) return undefined;
  const id = resolveModelLabel(top.languageId, registry);
  return id ? { id, source: "content", confidence: top.confidence } : undefined;
}

/** Runs the ML classifier in a lazily created Web Worker (model files load on first use). */
export function createWorkerClassifier(): ContentClassifier {
  let worker: Worker | undefined;
  let nextId = 0;
  const pending = new Map<
    number,
    { resolve: (r: ModelResult[]) => void; reject: (e: Error) => void }
  >();

  const getWorker = () => {
    if (worker) return worker;
    worker = new Worker(new URL("./content.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (
      event: MessageEvent<{ id: number; results?: ModelResult[]; error?: string }>,
    ) => {
      const { id, results, error } = event.data;
      const request = pending.get(id);
      pending.delete(id);
      if (error !== undefined) request?.reject(new Error(error));
      else request?.resolve(results ?? []);
    };
    return worker;
  };

  return {
    classify(content) {
      const id = nextId++;
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        getWorker().postMessage({ id, content });
      });
    },
    dispose() {
      worker?.terminate();
      worker = undefined;
      for (const request of pending.values()) request.reject(new Error("classifier disposed"));
      pending.clear();
    },
  };
}

/** Guesses a language from file content with VS Code's language-detection model. */
export class ContentDetector {
  readonly #registry: LanguageRegistry;
  readonly #classifier: ContentClassifier;
  readonly #options: Required<ContentDetectionOptions>;

  constructor(
    registry: LanguageRegistry,
    classifier: ContentClassifier = createWorkerClassifier(),
    options: ContentDetectionOptions = {},
  ) {
    this.#registry = registry;
    this.#classifier = classifier;
    this.#options = { ...DEFAULTS, ...options };
  }

  /** Resolves to a confident guess, or undefined (too short, ambiguous, or unknown label). */
  async detect(content: string): Promise<Detection | undefined> {
    if (content.replace(/\s/g, "").length < this.#options.minLength) return undefined;
    const results = await this.#classifier.classify(content.slice(0, this.#options.maxLength));
    return pickLanguage(results, this.#registry, this.#options);
  }

  dispose(): void {
    this.#classifier.dispose();
  }
}
